import type { ReactElement } from "react"
import { useState } from "react"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import BarOrg from "@onestore/hel/dist/components/organisms/BarOrg"
import GridOrg from "@onestore/hel/dist/components/organisms/GridOrg"
import OverlayContainerOrg, {
  headerTypes,
  sizes,
  startPositions,
} from "@onestore/hel/dist/components/organisms/OverlayContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import { uniqBy } from "lodash"
import LabeledToggleSwitch from "~/components/LabeledToggleSwitch"
import { findDeep } from "~/lib/findDeep"
import isEmpty from "~/lib/isEmpty"
import { useSquidexAssetsQuery } from "~/lib/squidex"
import AssetRow from "./AssetRow"

type PageAsset = {
  id: string
  url?: string
  alt?: string
  type: string
}
function SquidexA11yToolbar({ page }): ReactElement | null {
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const assets = uniqBy<PageAsset>(
    findDeep<PageAsset>(page, (object: PageAsset): boolean => {
      return (
        object.hasOwnProperty("id") &&
        object.hasOwnProperty("url") &&
        object.hasOwnProperty("alt") &&
        object.hasOwnProperty("type") &&
        object.hasOwnProperty("fileName") &&
        object.hasOwnProperty("mimeType") &&
        object.type !== "VIDEO"
      )
    }),
    "id"
  )

  const assetsResponse = useSquidexAssetsQuery(assets.map((asset) => asset.id))

  if (assetsResponse.isLoading) {
    return (
      <div
        style={{ position: "fixed", bottom: 0, left: 0, zIndex: 9999 }}
        data-test-id="squidex-a11y-toolbar"
      >
        <BarOrg color="errorLight">
          <ButtonAtm isLoading={true} title={"Wczytywanie"}>
            Wczytywanie
          </ButtonAtm>
        </BarOrg>
      </div>
    )
  }

  const allAssets = assetsResponse.data?.data.items ?? []

  const emptyAssets = allAssets.filter((asset) => {
    return isEmpty(asset?.metadata?.alt)
  })

  if (!allAssets || allAssets.length === 0) {
    return null
  }

  return (
    <>
      {isOpen ? (
        <OverlayContainerOrg
          startPosition={startPositions.RIGHT}
          size={sizes.LARGE}
          headerType={headerTypes.SHADOWED}
          onCloseButtonClick={() => {
            setIsOpen(false)
          }}
          header={
            <PushOrg>
              <GridOrg gutterSpace={1} columns={2}>
                <TextAtm typography="large2">
                  Aktualizacja opisów grafik na stronie
                </TextAtm>

                <LabeledToggleSwitch
                  id={"toggle-a11y-assets-switch"}
                  onClick={() => {
                    setShowAll(!showAll)
                  }}
                  isChecked={showAll}
                  label={"Wszystkie"}
                />
              </GridOrg>
            </PushOrg>
          }
        >
          {allAssets.map((asset) => (
            <AssetRow showAllAssets={showAll} key={asset.id} asset={asset} />
          ))}
        </OverlayContainerOrg>
      ) : null}

      <div
        style={{ position: "fixed", bottom: 0, left: 0, zIndex: 9999 }}
        data-test-id="squidex-a11y-toolbar"
      >
        <BarOrg color={emptyAssets.length > 0 ? "errorLight" : "successLight"}>
          <ButtonAtm
            title="Uzupełnij brakujące opisy"
            color="error"
            size="small"
            onClick={() => {
              setIsOpen(true)
            }}
          >
            Uzupełnij {emptyAssets.length} brakujących opisów grafik na stronie
          </ButtonAtm>
        </BarOrg>
      </div>
    </>
  )
}

function SquidexA11yToolbarWrapper({ page }): ReactElement | null {
  if (process.env.GATSBY_SQUIDEX_TOOLBAR !== "true") {
    return null
  }

  return <SquidexA11yToolbar page={page} />
}

export default SquidexA11yToolbarWrapper
